import { useMemo, useReducer, useState } from "react";
import type { Table } from "@tanstack/react-table";
import moment from "moment-timezone";
import { ChevronLeft, ChevronRight, SlidersHorizontal, Split } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DataTable,dateWithFormatSort, dateSort, type DataTableColumn } from "@/components/ui/DataTable/index";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { useListCustomers } from "@/lib/api/customer";
import type { Customer } from "@/lib/api/reports";
import { formatNumberToDisplay } from "@/lib/api/common";
import { CommodityOptions } from "@/lib/api/commodity";
import {
  type ListOtcInvoiceQueueQuery,
  type ListOtcInvoicesQuery,
  type OtcInvoiceDto,
  type OtcInvoiceQueueAllocationProjection,
  type OtcInvoiceQueueListItem,
  type OtcInvoiceQueueStatus,
  type OtcInvoiceStatus,
  type SendOtcInvoiceQueueEntriesResult,
  useArchiveOtcInvoices,
  useListOtcInvoiceQueue,
  useListOtcInvoices,
  useSendOtcInvoiceQueueEntries,
  useSkipOtcInvoiceQueueEntries,
} from "@/lib/api/otcInvoices";
import { CommonDestinationTimezone } from "@/lib/utils";
import {
  ArchiveOtcInvoicesRequestValidation,
  SendOtcInvoiceQueueEntriesRequestValidation,
  SkipOtcInvoiceQueueEntriesRequestValidation,
} from "@/lib/validation/otcInvoices";
import { IndeterminateCheckbox } from "@/section/cfr/components/IndeterminateCheckbox";
import { CustomerFormDialog, useBearStore as useCustomerBearStore } from "@/section/customer/ManageCustomers";
import { getAmendmentTypeDisplay } from "@/lib/api/otc/amendmentTypes";
import { BlockingReasonsDialog, ReasonDialog, SendResultDialog } from "./InvoiceActionDialogs";
import { InvoiceAllocationsDialog } from "./InvoiceAllocationsDialog";
import { DownloadOtcInvoiceButton } from "./DownloadOtcInvoiceButton";
import {
  InvoiceFiltersDialog,
  QueueFiltersDialog,
  type InvoiceFilters,
  type QueueFilters,
} from "./InvoiceFilterDialogs";

const PAGE_SIZE = 100;

type QueueAllocationRow = OtcInvoiceQueueListItem & {
  allocation: OtcInvoiceQueueAllocationProjection;
};

const emptyQueueFilters: QueueFilters = {
  customerId: "",
  contractNumber: "",
  createdDate: undefined,
};

const emptyInvoiceFilters: InvoiceFilters = {
  customerId: "",
  invoiceNumber: "",
  contractNumber: "",
  invoiceDate: undefined,
};

type FilterState<TFilters> = {
  applied: TFilters;
  draft: TFilters;
  isOpen: boolean;
};

type FilterAction<TFilters> =
  | { type: "open" }
  | { type: "close" }
  | { type: "change"; filters: TFilters }
  | { type: "apply" }
  | { type: "reset"; filters: TFilters };

function filterReducer<TFilters>(state: FilterState<TFilters>, action: FilterAction<TFilters>): FilterState<TFilters> {
  switch (action.type) {
    case "open":
      return { ...state, draft: state.applied, isOpen: true };
    case "close":
      return { ...state, isOpen: false };
    case "change":
      return { ...state, draft: action.filters };
    case "apply":
      return { applied: state.draft, draft: state.draft, isOpen: false };
    case "reset":
      return { applied: action.filters, draft: action.filters, isOpen: false };
  }
}

function apiDate(date: Date | undefined): string | undefined {
  return date
    ? moment.tz(date, CommonDestinationTimezone).format("YYYY-MM-DD")
    : undefined;
}

function uniqueQueueEntryIds(rows: QueueAllocationRow[]): string[] {
  return [...new Set(rows.map((row: QueueAllocationRow): string => row.queue_entry_id))];
}

function errorDescription(result: { message?: string; data?: unknown }): string {
  const context = result.data === undefined ? "" : ` ${JSON.stringify(result.data)}`;
  return `${result.message || "The request failed."}${context}`;
}

function PaginationControls({
  page,
  hasNextPage,
  isFetching,
  onPageChange,
}: {
  page: number;
  hasNextPage: boolean;
  isFetching: boolean;
  onPageChange: (page: number) => void;
}) {
  return (
    <div className="flex items-center justify-between py-2">
      <Button
        variant="outline"
        size="sm"
        onClick={() => onPageChange(page - 1)}
        disabled={page === 1 || isFetching}
      >
        <ChevronLeft className="mr-1 h-4 w-4" /> Previous
      </Button>
      <span className="text-sm text-muted-foreground">Page {page}</span>
      <Button
        variant="outline"
        size="sm"
        onClick={() => onPageChange(page + 1)}
        disabled={!hasNextPage || isFetching}
      >
        Next <ChevronRight className="ml-1 h-4 w-4" />
      </Button>
    </div>
  );
}

export function OtcInvoicesSection() {
  const customersResult = useListCustomers();
  const customers = customersResult.data?.data ?? [];
  const [isCustomerDialogOpen, setIsCustomerDialogOpen] = useState(false);
  const setCustomer = useCustomerBearStore((state) => state.setCustomer);
  const customerOptions = customers.map((customer: Customer) => ({
    value: customer.id,
    label: customer.long_name,
  }));
  const openCustomer = (customerId: string): void => {
    const customer = customers.find((item: Customer) => item.id === customerId);
    if (!customer) return;
    setCustomer(customer as any);
    setIsCustomerDialogOpen(true);
  };

  return (
    <>
      <Tabs defaultValue="staged" className="w-full">
        <TabsList className="my-2">
          <TabsTrigger value="staged">Staged Invoices</TabsTrigger>
          <TabsTrigger value="list">List Invoices</TabsTrigger>
        </TabsList>
        <TabsContent value="staged">
          <StagedInvoices customerOptions={customerOptions} onCustomerClick={openCustomer} />
        </TabsContent>
        <TabsContent value="list">
          <InvoiceList customerOptions={customerOptions} />
        </TabsContent>
      </Tabs>
      <CustomerFormDialog open={isCustomerDialogOpen} onOpenChange={setIsCustomerDialogOpen} />
    </>
  );
}

function StagedInvoices({
  customerOptions,
  onCustomerClick,
}: {
  customerOptions: Array<{ value: string; label: string }>;
  onCustomerClick: (customerId: string) => void;
}) {
  const { toast } = useToast();
  const [status, setStatus] = useState<OtcInvoiceQueueStatus>("queued");
  const [page, setPage] = useState(1);
  const [filterState, dispatchFilters] = useReducer(
    filterReducer<QueueFilters>,
    { applied: emptyQueueFilters, draft: emptyQueueFilters, isOpen: false },
  );
  const [selectionVersion, setSelectionVersion] = useState(0);
  const [selectedEntry, setSelectedEntry] = useState<QueueAllocationRow | null>(null);
  const [allocationEntry, setAllocationEntry] = useState<OtcInvoiceQueueListItem | null>(null);
  const [skipIds, setSkipIds] = useState<string[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [isSkipping, setIsSkipping] = useState(false);
  const [sendResult, setSendResult] = useState<SendOtcInvoiceQueueEntriesResult | null>(null);

  const sendEntries = useSendOtcInvoiceQueueEntries();
  const skipEntries = useSkipOtcInvoiceQueueEntries();

  const query: ListOtcInvoiceQueueQuery = {
    statuses: [status],
    page,
    page_size: PAGE_SIZE,
    ...(filterState.applied.customerId ? { customer_id: filterState.applied.customerId } : {}),
    ...(filterState.applied.contractNumber.trim() ? { contract_number: filterState.applied.contractNumber.trim() } : {}),
    ...(apiDate(filterState.applied.createdDate?.from) ? { created_at_from: apiDate(filterState.applied.createdDate?.from) } : {}),
    ...(apiDate(filterState.applied.createdDate?.to) ? { created_at_to: apiDate(filterState.applied.createdDate?.to) } : {}),
  };
  const queueResult = useListOtcInvoiceQueue(query);
  const queueData = queueResult.data?.success ? queueResult.data.data : null;
  const entries = queueData?.items ?? [];
  const rows = useMemo(
    (): QueueAllocationRow[] => entries.flatMap(
      (entry: OtcInvoiceQueueListItem): QueueAllocationRow[] => entry.allocations.map(
        (allocation: OtcInvoiceQueueAllocationProjection): QueueAllocationRow => ({ ...entry, allocation }),
      ),
    ),
    [entries],
  );

  const clearSelection = (): void => setSelectionVersion((version: number): number => version + 1);

  const runSend = async (queueEntryIds: string[]): Promise<void> => {
    const parsed = SendOtcInvoiceQueueEntriesRequestValidation.safeParse({ queue_entry_ids: queueEntryIds });
    if (!parsed.success || isSending) return;
    try {
        setIsSending(true);
        const result = await sendEntries(parsed.data);
        clearSelection();
        if (!result.success) {
            toast({ title: "Failed to send OTC invoices", description: errorDescription(result), variant: "destructive" });
            return;
        }
        setSendResult(result.data);
    } catch(error) {
        toast({ title: "Failed to send OTC invoices", description: "", variant: "destructive" });
    } finally {
        setIsSending(false);
    }
  };

  const runSkip = async (reason: string): Promise<void> => {
    const parsed = SkipOtcInvoiceQueueEntriesRequestValidation.safeParse({
      items: skipIds.map((queue_entry_id: string) => ({ queue_entry_id, reason })),
    });
    if (!parsed.success || isSkipping) return;
    try {
        setIsSkipping(true);
        const result = await skipEntries(parsed.data);
        if (!result.success) {
            toast({ title: "Failed to skip OTC invoices", description: errorDescription(result), variant: "destructive" });
            return;
        }
        toast({ title: `Skipped ${result.data.skipped_count} invoice queue entr${result.data.skipped_count === 1 ? "y" : "ies"}` });
        setSkipIds([]);
        clearSelection();
    } catch(error) {
        toast({ title: "Failed to skip OTC invoices", description: "", variant: "destructive" });
    } finally {
        setIsSkipping(false);
    }
  };

  const columns = useMemo((): DataTableColumn<QueueAllocationRow>[] => {
    const result: DataTableColumn<QueueAllocationRow>[] = [];
    if (status === "queued") {
      result.push({
        id: "selection-box",
        enableFiltering: false,
        enableSorting: false,
        enableGrouping: false,
        disableRowClick: true,
        header: ({ table }) => (
          <IndeterminateCheckbox
            className="mt-2 ml-1"
            checked={table.getIsAllRowsSelected()}
            indeterminate={table.getIsSomeRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        ),
        cell: ({ row }) => (
          <IndeterminateCheckbox
            className="ml-1"
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            indeterminate={row.getIsSomeSelected()}
            onChange={row.getToggleSelectedHandler()}
          />
        ),
        size: 50,
      });
    }

    if (status === "queued") {
      result.push(
        {
          id: "allocation_actions",
          header: "",
          enableFiltering: false,
          enableSorting: false,
          enableGrouping: false,
          disableRowClick: true,
          cell: ({ row }) => (
            <Button type="button" size="sm" variant="outline" onClick={() => setAllocationEntry(row.original)}>
              <Split className="mr-2 h-4 w-4" /> Pricing Split
            </Button>
          ),
          size: 150,
        },
        {
          id: "error_count",
          accessorFn: (row: QueueAllocationRow) => row.send_blocking_reasons.length,
          header: "Errors",
        },
      );
    }

    result.push(
      { id: "contract_number", accessorFn: (row: QueueAllocationRow) => row.contract_number ?? "", header: "Contract #" },
      { id: "layer_no", accessorFn: (row: QueueAllocationRow) => row.layer_no, header: "Layer #" },
      { id: "amendment_type", accessorFn: (row: QueueAllocationRow) => getAmendmentTypeDisplay(row.amendment_type), header: "Amendment Type" },
      {
        id: "is_correction",
        accessorFn: (row: QueueAllocationRow) => row.is_correction ? "Correction" : "",
        header: "Correction",
        cell: ({ row }) => row.original.is_correction
          ? <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">Correction</Badge>
          : null,
      },
      { id: "percentage", accessorFn: (row: QueueAllocationRow) => `${row.allocation.percentage}%`, header: "Percentage" },
      {
        id: "customer",
        accessorFn: (row: QueueAllocationRow) => row.allocation.Customer.long_name,
        header: "Customer",
        size: 200,
        cell: ({ row }) => (
          <span className="text-blue-600 underline">
            {row.original.allocation.Customer.long_name}
          </span>
        ),
        onCellClick: (row: QueueAllocationRow) => onCustomerClick(row.allocation.customer_id),
      },
      { id: "commodity", accessorFn: (row: QueueAllocationRow) => CommodityOptions.find((opt) => opt.value === row.commodity)?.label ?? row.commodity, header: "Commodity" },
      {
        id: "invoice_quantity",
        accessorFn: (row: QueueAllocationRow) => row.allocation.invoice_quantity,
        header: "Invoice Quantity",
        cell: ({ row }) => formatNumberToDisplay(row.original.allocation.invoice_quantity, { maximumFractionDigits: 4 }),
        size: 150
      },
      {
        id: "contract_fee",
        accessorFn: (row: QueueAllocationRow) => row.contract_fee,
        header: "Contract Fee",
        cell: ({ row }) => formatNumberToDisplay(row.original.contract_fee, { isCurrency: true, maximumFractionDigits: 4 }),
      },
      {
        id: "invoice_amount",
        accessorFn: (row: QueueAllocationRow) => row.allocation.invoice_amount,
        header: "Invoice Amount",
        cell: ({ row }) => formatNumberToDisplay(row.original.allocation.invoice_amount, { isCurrency: true }),
        size: 150
      },
      { id: "description", accessorFn: (row: QueueAllocationRow) => row.description ?? "", header: "Description" },
      {
        id: "created_at",
        sortingFn: dateWithFormatSort("YYYY-MM-DD HH:mm:ss.ssss"),
        accessorFn: (row: QueueAllocationRow) => row.created_at,
        header: "Created At",
        columnFilterType: "date",
        filterDateTimezone: CommonDestinationTimezone,
        filterDisplayFn: (value: unknown) => moment.tz(value as string, CommonDestinationTimezone).format("YYYY/MM/DD"),
        filterParseDateFn: (value: unknown) => moment.tz(value as string, CommonDestinationTimezone),
        cell: ({ row }) => moment.tz(row.original.created_at, CommonDestinationTimezone).format("MM/DD/YYYY"),
      },
    );
    return result;
  }, [status, isSending, onCustomerClick]);

  const bulkButtons = status === "queued"
    ? [({ table }: { table: Table<QueueAllocationRow> }) => {
        const selectedRows = table.getFilteredSelectedRowModel().rows.map(
          (row: { original: QueueAllocationRow }): QueueAllocationRow => row.original,
        );
        const queueEntryIds = uniqueQueueEntryIds(selectedRows);
        return (
          <div className="flex gap-2">
            <Button variant="outline" disabled={queueEntryIds.length === 0 || isSending} onClick={() => runSend(queueEntryIds)}>
              Bulk Send ({queueEntryIds.length})
            </Button>
            <Button variant="outline" disabled={queueEntryIds.length === 0 || isSkipping} onClick={() => setSkipIds(queueEntryIds)}>
              Bulk Skip ({queueEntryIds.length})
            </Button>
          </div>
        );
      }]
    : undefined;

  const applyFilters = (): void => {
    dispatchFilters({ type: "apply" });
    setPage(1);
    clearSelection();
  };
  const resetFilters = (): void => {
    dispatchFilters({ type: "reset", filters: emptyQueueFilters });
    setPage(1);
    clearSelection();
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-2">
        <div className="grid gap-1"><Label>Status</Label><Combobox value={status} onValueChange={(value: string) => { setStatus(value as OtcInvoiceQueueStatus); setPage(1); clearSelection(); }} options={[{ value: "queued", label: "Queued" }, { value: "sent", label: "Sent" }, { value: "skipped", label: "Skipped" }]} /></div>
        <Button size="sm" variant="outline" onClick={() => dispatchFilters({ type: "open" })}><SlidersHorizontal className="mr-2 h-4 w-4" />Filters</Button>
        <Button size="sm" variant="outline" onClick={resetFilters}>Reset Filters</Button>
        {queueResult.isFetching && <span className="self-center text-sm text-muted-foreground">Loading...</span>}
      </div>
      <QueueFiltersDialog open={filterState.isOpen} filters={filterState.draft} customerOptions={customerOptions} onChange={(filters: QueueFilters) => dispatchFilters({ type: "change", filters })} onClose={() => dispatchFilters({ type: "close" })} onApply={applyFilters} />
      {!queueResult.isLoading && queueResult.data && !queueResult.data.success ? <p className="text-sm text-destructive">{errorDescription(queueResult.data)}</p> : null}
      <DataTable
        key={`queue-${status}-${page}-${selectionVersion}`}
        stickyHeaders
        tableClassName="max-h-[calc(100vh-200px)] overflow-y-scroll"
        data={rows}
        columns={columns}
        enableSorting
        enableMultiSort
        enableFiltering
        enableGrouping
        defaultDataTableGroupedToExpanded
        enableColumnVisibility
        enableMultiSelect={status === "queued"}
        customToolbarButtons={bulkButtons}
        filterStyle="select"
        filterMode="internal"
        enableColumnResizing
        enableColumnPinning
        enableMultiFilter
        defaultCondensed
        enableVirtualization
        virtualizationRowHeight={65}
        virtualizationOverscan={2}
        rowBgColor={(row: QueueAllocationRow) => status === "queued" && row.send_blocking_reasons.length > 0 ? { preset: "light-red" } : undefined}
        legend={status === "queued" ? [{ color: { preset: "light-red" }, label: "Blocked" }] : undefined}
        emptyMessage={queueResult.isLoading ? "Loading..." : `No ${status} invoice queue entries found`}
        rowClickable={status === "queued"}
        onRowClick={(row: QueueAllocationRow) => setSelectedEntry(row)}
      />
      <PaginationControls page={page} hasNextPage={queueData?.has_next_page ?? false} isFetching={queueResult.isFetching} onPageChange={(nextPage: number) => { setPage(nextPage); clearSelection(); }} />
      <BlockingReasonsDialog entry={selectedEntry} customerName={selectedEntry?.allocation.Customer.long_name ?? ""} onClose={() => setSelectedEntry(null)} />
      <InvoiceAllocationsDialog entry={allocationEntry} customerOptions={customerOptions} onClose={() => setAllocationEntry(null)} />
      <ReasonDialog open={skipIds.length > 0} title="Bulk Skip Invoice Queue Entries" description={`Skip ${skipIds.length} selected queue entr${skipIds.length === 1 ? "y" : "ies"}. The same reason will be applied to each.`} submitLabel="Skip Entries" isSubmitting={isSkipping} onClose={() => setSkipIds([])} onSubmit={runSkip} />
      <SendResultDialog result={sendResult} onClose={() => setSendResult(null)} />
    </div>
  );
}

function InvoiceList({
  customerOptions,
}: {
  customerOptions: Array<{ value: string; label: string }>;
}) {
  const { toast } = useToast();
  const [status, setStatus] = useState<OtcInvoiceStatus>("active");
  const [page, setPage] = useState(1);
  const [filterState, dispatchFilters] = useReducer(
    filterReducer<InvoiceFilters>,
    { applied: emptyInvoiceFilters, draft: emptyInvoiceFilters, isOpen: false },
  );
  const [selectionVersion, setSelectionVersion] = useState(0);
  const [archiveIds, setArchiveIds] = useState<string[]>([]);
  const [isArchiving, setIsArchiving] = useState(false);
  const archiveInvoices = useArchiveOtcInvoices();

  const query: ListOtcInvoicesQuery = {
    statuses: [status],
    page,
    page_size: PAGE_SIZE,
    ...(filterState.applied.customerId ? { customer_id: filterState.applied.customerId } : {}),
    ...(filterState.applied.invoiceNumber.trim() ? { invoice_number: filterState.applied.invoiceNumber.trim() } : {}),
    ...(filterState.applied.contractNumber.trim() ? { contract_number: filterState.applied.contractNumber.trim() } : {}),
    ...(apiDate(filterState.applied.invoiceDate?.from) ? { invoice_date_from: apiDate(filterState.applied.invoiceDate?.from) } : {}),
    ...(apiDate(filterState.applied.invoiceDate?.to) ? { invoice_date_to: apiDate(filterState.applied.invoiceDate?.to) } : {}),
  };
  const invoicesResult = useListOtcInvoices(query);
  const invoiceData = invoicesResult.data?.success ? invoicesResult.data.data : null;
  const invoices = invoiceData?.items ?? [];
  const clearSelection = (): void => setSelectionVersion((version: number): number => version + 1);

  const runArchive = async (reason: string): Promise<void> => {
    const parsed = ArchiveOtcInvoicesRequestValidation.safeParse({
      items: archiveIds.map((invoice_id: string) => ({ invoice_id, reason })),
    });
    if (!parsed.success || isArchiving) return;
    try {
        setIsArchiving(true);
        const result = await archiveInvoices(parsed.data);
        if (!result.success) {
            toast({ title: "Failed to archive OTC invoices", description: errorDescription(result), variant: "destructive" });
            return;
        }
        toast({ title: `Archived ${result.data.archived_count} invoice${result.data.archived_count === 1 ? "" : "s"}` });
        setArchiveIds([]);
        clearSelection();
    }
    catch(error) {
      toast({ title: "Failed to archive OTC invoices", description: "", variant: "destructive" });
    } finally {
        setIsArchiving(false);
    }
  };

  const columns = useMemo((): DataTableColumn<OtcInvoiceDto>[] => {
    const result: DataTableColumn<OtcInvoiceDto>[] = [];
    if (status === "active") {
      result.push({
        id: "selection-box",
        enableFiltering: false,
        enableSorting: false,
        disableRowClick: true,
        header: ({ table }) => <IndeterminateCheckbox className="mt-2 ml-1" checked={table.getIsAllRowsSelected()} indeterminate={table.getIsSomeRowsSelected()} onChange={table.getToggleAllRowsSelectedHandler()} />,
        cell: ({ row }) => <IndeterminateCheckbox className="ml-1" checked={row.getIsSelected()} disabled={!row.getCanSelect()} indeterminate={row.getIsSomeSelected()} onChange={row.getToggleSelectedHandler()} />,
        size: 50,
      });
    }
    result.push(
      {
        id: "download",
        header: "",
        enableFiltering: false,
        enableSorting: false,
        disableRowClick: true,
        cell: ({ row }) => <DownloadOtcInvoiceButton invoiceId={row.original.id} />,
        size: 160,
      },
      { id: "invoice_number", accessorFn: (row: OtcInvoiceDto) => row.invoice_number, header: "Invoice Number" },
      {
        id: "invoice_date",
        accessorFn: (row: OtcInvoiceDto) => row.invoice_date,
        header: "Invoice Date",
        columnFilterType: "date",
        filterDateTimezone: CommonDestinationTimezone,
        filterDisplayFn: (value: unknown) => moment.tz(value as string, CommonDestinationTimezone).format("YYYY/MM/DD"),
        filterParseDateFn: (value: unknown) => moment.tz(value as string, CommonDestinationTimezone),
        cell: ({ row }) => moment.tz(row.original.invoice_date, CommonDestinationTimezone).format("MM/DD/YYYY"),
      },
      { id: "contract_number", accessorFn: (row: OtcInvoiceDto) => row.contract_number, header: "Contract #", size: 150 },
      { id: "layer_no", accessorFn: (row: OtcInvoiceDto) => row.layer_no, header: "Layer #" },
      { id: "amendment_type", accessorFn: (row: OtcInvoiceDto) => getAmendmentTypeDisplay(row.amendment_type), header: "Amendment Type", size: 150 },
      {
        id: "is_correction",
        accessorFn: (row: OtcInvoiceDto) => row.is_correction ? "Correction" : "",
        header: "Correction",
        cell: ({ row }) => row.original.is_correction
          ? <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">Correction</Badge>
          : null,
      },
      {
        id: "customer_name",
        accessorFn: (row: OtcInvoiceDto) => row.customer_name,
        header: "Customer",
        size: 200,
      },
      { id: "commodity", accessorFn: (row: OtcInvoiceDto) => CommodityOptions.find((opt) => opt.value === row.commodity)?.label ?? row.commodity, header: "Commodity" },
      { id: "invoice_quantity", accessorFn: (row: OtcInvoiceDto) => row.invoice_quantity, header: "Invoice Quantity", cell: ({ row }) => formatNumberToDisplay(row.original.invoice_quantity, { maximumFractionDigits: 4 }), size: 150 },
      { id: "contract_fee", accessorFn: (row: OtcInvoiceDto) => row.contract_fee, header: "Contract Fee", cell: ({ row }) => formatNumberToDisplay(row.original.contract_fee, { isCurrency: true, maximumFractionDigits: 4 }) },
      { id: "invoice_amount", accessorFn: (row: OtcInvoiceDto) => row.invoice_amount, header: "Invoice Amount", cell: ({ row }) => formatNumberToDisplay(row.original.invoice_amount, { isCurrency: true }), size: 150 },
      { id: "description", accessorFn: (row: OtcInvoiceDto) => row.description, header: "Description" },
      {
        id: "sent_at",
        accessorFn: (row: OtcInvoiceDto) => row.sent_at,
        sortingFn: dateWithFormatSort("YYYY-MM-DD HH:mm:ss.ssss"),
        header: "Sent At",
        columnFilterType: "date",
        filterDateTimezone: CommonDestinationTimezone,
        filterDisplayFn: (value: unknown) => moment.tz(value as string, CommonDestinationTimezone).format("YYYY/MM/DD hh:mm"),
        filterParseDateFn: (value: unknown) => moment.tz(value as string, CommonDestinationTimezone),
        cell: ({ row }) => moment.tz(row.original.sent_at, CommonDestinationTimezone).format("MM/DD/YYYY hh:mm a"),
        size: 200,
      },
    );
    return result;
  }, [status]);

  const bulkButtons = status === "active"
    ? [({ table }: { table: Table<OtcInvoiceDto> }) => {
        const selectedInvoices = table.getFilteredSelectedRowModel().rows.map(
          (row: { original: OtcInvoiceDto }): OtcInvoiceDto => row.original,
        );
        return <Button variant="outline" disabled={selectedInvoices.length === 0 || isArchiving} onClick={() => setArchiveIds(selectedInvoices.map((invoice: OtcInvoiceDto): string => invoice.id))}>Bulk Archive ({selectedInvoices.length})</Button>;
      }]
    : undefined;

  const applyFilters = (): void => { dispatchFilters({ type: "apply" }); setPage(1); clearSelection(); };
  const resetFilters = (): void => { dispatchFilters({ type: "reset", filters: emptyInvoiceFilters }); setPage(1); clearSelection(); };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-2">
        <div className="grid gap-1"><Label>Status</Label><Combobox value={status} onValueChange={(value: string) => { setStatus(value as OtcInvoiceStatus); setPage(1); clearSelection(); }} options={[{ value: "active", label: "Sent" }, { value: "archived", label: "Archived" }]} /></div>
        <Button size="sm" variant="outline" onClick={() => dispatchFilters({ type: "open" })}><SlidersHorizontal className="mr-2 h-4 w-4" />Filters</Button>
        <Button size="sm" variant="outline" onClick={resetFilters}>Reset Filters</Button>
        {invoicesResult.isFetching && <span className="self-center text-sm text-muted-foreground">Loading...</span>}
      </div>
      <InvoiceFiltersDialog open={filterState.isOpen} filters={filterState.draft} customerOptions={customerOptions} onChange={(filters: InvoiceFilters) => dispatchFilters({ type: "change", filters })} onClose={() => dispatchFilters({ type: "close" })} onApply={applyFilters} />
      {!invoicesResult.isLoading && invoicesResult.data && !invoicesResult.data.success ? <p className="text-sm text-destructive">{errorDescription(invoicesResult.data)}</p> : null}
      <DataTable
        key={`invoices-${status}-${page}-${selectionVersion}`}
        stickyHeaders
        tableClassName="max-h-[calc(100vh-500px)] overflow-y-scroll"
        data={invoices}
        columns={columns}
        enableSorting
        enableMultiSort
        enableFiltering
        enableColumnVisibility
        enableMultiSelect={status === "active"}
        customToolbarButtons={bulkButtons}
        enableColumnResizing
        enableColumnPinning
        enableMultiFilter
        enableVirtualization
        virtualizationRowHeight={65}
        virtualizationOverscan={2}
        filterStyle="select"
        filterMode="internal"
        defaultSorting={[{ id: "invoice_date", desc: true }, { id: "sent_at", desc: true }]}
        emptyMessage={invoicesResult.isLoading ? "Loading..." : `No ${status === "active" ? "sent" : "archived"} invoices found`}
      />
      <PaginationControls page={page} hasNextPage={invoiceData?.has_next_page ?? false} isFetching={invoicesResult.isFetching} onPageChange={(nextPage: number) => { setPage(nextPage); clearSelection(); }} />
      <ReasonDialog open={archiveIds.length > 0} title="Bulk Archive OTC Invoices" description={`Archive ${archiveIds.length} selected invoice${archiveIds.length === 1 ? "" : "s"}. The same reason will be applied to each.`} submitLabel="Archive Invoices" isSubmitting={isArchiving} onClose={() => setArchiveIds([])} onSubmit={runArchive} />
    </div>
  );
}
